import { AlertCircle, Check, Crown, ExternalLink, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { trpc, type RouterOutputs } from '@/trpc/client'

function formatBytes(n: bigint | number): string {
	const bytes = typeof n === 'bigint' ? Number(n) : n
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
	if (bytes < 1024 * 1024 * 1024)
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
	return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

type BillingData = RouterOutputs['billing']['getPlan']

export function BillingPage() {
	const utils = trpc.useUtils()
	const billing = trpc.billing.getPlan.useQuery()
	const current = trpc.organizations.getCurrent.useQuery()

	const refetchAll = async () => {
		await Promise.all([
			utils.billing.getPlan.invalidate(),
			utils.organizations.getCurrent.invalidate(),
		])
	}

	const checkout = trpc.billing.createCheckoutUrl.useMutation({
		onSuccess: (result) => {
			if (result.status === 'unavailable') {
				toast.error('Billing is not configured on this server.')
				return
			}
			window.location.href = result.url
		},
		onError: (err) => toast.error(err.message || 'Could not start checkout.'),
	})

	const portal = trpc.billing.createPortalUrl.useMutation({
		onSuccess: (result) => {
			if (result.status === 'unavailable') {
				toast.error('Billing is not configured on this server.')
				return
			}
			if (result.status === 'no_customer') {
				toast.info(
					'No billing details yet — upgrade once to set up the portal.',
				)
				return
			}
			window.location.href = result.url
		},
		onError: (err) =>
			toast.error(err.message || 'Could not open billing portal.'),
	})

	const mockUpgrade = trpc.billing.mockUpgrade.useMutation({
		onSuccess: async () => {
			// Confirm the action before awaiting refetch so a failed invalidation
			// can't swallow the success signal.
			toast.success('Upgraded to Pro (mock mode).')
			try {
				await refetchAll()
			} catch {
				toast.warning('Plan updated — refresh to see the latest usage.')
			}
		},
		onError: (err) => toast.error(err.message || 'Mock upgrade failed.'),
	})

	const mockDowngrade = trpc.billing.mockDowngrade.useMutation({
		onSuccess: async () => {
			toast.message('Downgraded to Free (mock mode).')
			try {
				await refetchAll()
			} catch {
				toast.warning('Plan updated — refresh to see the latest usage.')
			}
		},
		onError: (err) => toast.error(err.message || 'Mock downgrade failed.'),
	})

	if (billing.isPending || current.isPending) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-32 w-full" />
				<Skeleton className="h-80 w-full" />
			</div>
		)
	}

	if (billing.isError || current.isError) {
		const message =
			billing.error?.message ?? current.error?.message ?? 'Unknown error'
		return (
			<Alert variant="destructive">
				<AlertCircle />
				<AlertTitle>Couldn't load billing</AlertTitle>
				<AlertDescription>{message}</AlertDescription>
			</Alert>
		)
	}

	const data = billing.data
	const org = current.data
	const isPro = data.plan === 'pro'
	const isOwner = org.isOwner
	const usedBytes = BigInt(org.storageBytesUsed)
	const limitBytes = BigInt(data.limits.storageBytes)
	const itemsUsed = org.itemsCount
	const itemsLimit = data.limits.itemLimit

	const upgradeMonthly = () =>
		data.billingMode === 'mock'
			? mockUpgrade.mutate({ interval: 'monthly' })
			: checkout.mutate({ interval: 'monthly' })
	const upgradeYearly = () =>
		data.billingMode === 'mock'
			? mockUpgrade.mutate({ interval: 'yearly' })
			: checkout.mutate({ interval: 'yearly' })

	const upgradePending =
		checkout.isPending || mockUpgrade.isPending
	const downgradePending = portal.isPending || mockDowngrade.isPending

	return (
		<div className="space-y-6">
			{data.billingMode === 'unavailable' ? (
				<Alert>
					<AlertCircle />
					<AlertTitle>Billing isn't configured on this server</AlertTitle>
					<AlertDescription>
						You can still use the Free plan — upgrades will become available
						once the operator wires up Stripe.
					</AlertDescription>
				</Alert>
			) : null}

			{data.billingMode === 'mock' ? (
				<Alert>
					<AlertCircle />
					<AlertTitle>Mock billing mode</AlertTitle>
					<AlertDescription>
						Upgrades and downgrades on this server are simulated — no real
						charge is made. Set Stripe keys to enable real billing.
					</AlertDescription>
				</Alert>
			) : null}

			<Card>
				<CardContent className="space-y-4 p-5">
					<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
						<div>
							<div className="flex items-center gap-2">
								<span className="text-xs uppercase tracking-wide text-muted-foreground">
									Current plan
								</span>
								{isPro ? (
									<span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
										<Crown className="size-3" />
										Pro
									</span>
								) : (
									<span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
										Free
									</span>
								)}
							</div>
							{data.status !== 'active' && data.status !== 'trialing' ? (
								<p className="mt-1 text-xs text-destructive">
									Subscription status: {data.status}
								</p>
							) : null}
							{data.cancelAtPeriodEnd && data.currentPeriodEnd ? (
								<p className="mt-1 text-xs text-muted-foreground">
									Cancels on{' '}
									{new Date(data.currentPeriodEnd).toLocaleDateString()}
								</p>
							) : null}
						</div>
						{isOwner && isPro && data.billingMode === 'stripe' ? (
							<Button
								variant="outline"
								onClick={() => portal.mutate()}
								disabled={downgradePending}
							>
								<ExternalLink className="size-4" />
								{portal.isPending ? 'Opening…' : 'Manage billing'}
							</Button>
						) : null}
					</div>

					<div className="grid gap-4 pt-2 sm:grid-cols-3">
						<UsageRow
							label="Items"
							used={itemsUsed}
							limit={itemsLimit}
							format={(n) => n.toString()}
						/>
						<UsageRow
							label="Storage"
							used={Number(usedBytes)}
							limit={Number(limitBytes)}
							format={(n) => formatBytes(n)}
						/>
						<UsageRow
							label="Members"
							used={1}
							limit={data.limits.maxMembers}
							format={(n) => n.toString()}
						/>
					</div>
				</CardContent>
			</Card>

			<PlanComparison
				data={data}
				isOwner={isOwner}
				onUpgradeMonthly={upgradeMonthly}
				onUpgradeYearly={upgradeYearly}
				onMockDowngrade={() => mockDowngrade.mutate()}
				onPortal={() => portal.mutate()}
				upgradePending={upgradePending}
				downgradePending={downgradePending}
			/>
		</div>
	)
}

type PlanBullet =
	| { text: string; included: true; highlight?: boolean; comingSoon?: boolean }
	| { text: string; included: false }

interface PlanCardDef {
	id: 'free' | 'pro'
	name: string
	priceLine: string
	subline: string
	bullets: PlanBullet[]
	accent?: boolean
}

function PlanComparison({
	data,
	isOwner,
	onUpgradeMonthly,
	onUpgradeYearly,
	onMockDowngrade,
	onPortal,
	upgradePending,
	downgradePending,
}: {
	data: BillingData
	isOwner: boolean
	onUpgradeMonthly: () => void
	onUpgradeYearly: () => void
	onMockDowngrade: () => void
	onPortal: () => void
	upgradePending: boolean
	downgradePending: boolean
}) {
	const isPro = data.plan === 'pro'

	const free: PlanCardDef = {
		id: 'free',
		name: 'Free',
		priceLine: '$0',
		subline: 'forever',
		bullets: [
			{ text: '20 items per organization', included: true },
			{ text: '100 MB of file storage', included: true },
			{ text: 'Solo workspace (1 member)', included: true },
			{ text: '1 organization', included: true },
			{ text: 'Receipt OCR auto-extract', included: false },
			{ text: 'Invite teammates', included: false },
			{ text: 'Export to CSV / ZIP', included: false },
		],
	}

	const pro: PlanCardDef = {
		id: 'pro',
		name: 'Pro',
		priceLine: '$9',
		subline: 'per month, billed monthly or yearly',
		accent: true,
		bullets: [
			{ text: '10,000 items per organization', included: true },
			{ text: '5 GB of file storage', included: true },
			{
				text: 'Receipt OCR powered by Anthropic Claude',
				included: true,
				highlight: true,
				comingSoon: true,
			},
			{ text: 'Up to 10 members per organization', included: true },
			{ text: 'Up to 5 organizations', included: true },
			{ text: 'Invite teammates with email links', included: true },
			{ text: 'Export to CSV / ZIP', included: true },
		],
	}

	return (
		<div className="space-y-3">
			<div>
				<h2 className="text-sm font-semibold">Choose a plan</h2>
				<p className="text-xs text-muted-foreground">
					Upgrade anytime. Downgrade keeps your data — Pro features just stop
					applying.
				</p>
			</div>
			<div className="grid gap-4 sm:grid-cols-2">
				<PlanCard
					plan={free}
					isCurrent={!isPro}
					action={
						isPro && isOwner ? (
							data.billingMode === 'mock' ? (
								<Button
									variant="outline"
									className="w-full"
									onClick={onMockDowngrade}
									disabled={downgradePending}
								>
									{downgradePending ? 'Downgrading…' : 'Downgrade to Free'}
								</Button>
							) : data.billingMode === 'stripe' ? (
								<Button
									variant="outline"
									className="w-full"
									onClick={onPortal}
									disabled={downgradePending}
								>
									<ExternalLink className="size-4" />
									{downgradePending ? 'Opening…' : 'Manage in Stripe'}
								</Button>
							) : null
						) : null
					}
				/>
				<PlanCard
					plan={pro}
					isCurrent={isPro}
					action={
						!isPro && isOwner ? (
							data.billingMode === 'unavailable' ? (
								<Button className="w-full" disabled>
									Billing not configured
								</Button>
							) : (
								<div className="flex w-full flex-col gap-2">
									<Button
										className="w-full"
										onClick={onUpgradeMonthly}
										disabled={upgradePending}
									>
										{upgradePending
											? 'Loading…'
											: data.billingMode === 'mock'
												? 'Upgrade to Pro (mock)'
												: 'Upgrade to Pro — Monthly'}
									</Button>
									<button
										type="button"
										onClick={onUpgradeYearly}
										disabled={upgradePending}
										className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
									>
										or pay yearly (2 months free)
									</button>
								</div>
							)
						) : null
					}
				/>
			</div>
		</div>
	)
}

function PlanCard({
	plan,
	isCurrent,
	action,
}: {
	plan: PlanCardDef
	isCurrent: boolean
	action: React.ReactNode
}) {
	return (
		<Card
			className={cn(
				'flex flex-col',
				plan.accent && !isCurrent && 'border-primary/40 shadow-sm',
			)}
		>
			<CardContent className="flex flex-1 flex-col gap-5 p-5">
				<div className="flex items-start justify-between gap-2">
					<div>
						<div className="flex items-center gap-2">
							{plan.id === 'pro' ? (
								<Crown className="size-4 text-amber-500" />
							) : (
								<Sparkles className="size-4 text-muted-foreground" />
							)}
							<h3 className="text-base font-semibold">{plan.name}</h3>
						</div>
						<div className="mt-2 flex items-baseline gap-1.5">
							<span className="text-2xl font-semibold">{plan.priceLine}</span>
							<span className="text-xs text-muted-foreground">
								{plan.subline}
							</span>
						</div>
					</div>
					{isCurrent ? (
						<span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
							Current
						</span>
					) : null}
				</div>

				<ul className="flex-1 space-y-2 text-sm">
					{plan.bullets.map((b) => (
						<li
							key={b.text}
							className={cn(
								'flex items-start gap-2',
								!b.included && 'text-muted-foreground/60',
							)}
						>
							{b.included && b.highlight ? (
								<Sparkles className="size-4 shrink-0 mt-0.5 text-amber-500" />
							) : (
								<Check
									className={cn(
										'size-4 shrink-0 mt-0.5',
										b.included ? 'text-primary' : 'text-muted-foreground/40',
									)}
								/>
							)}
							<span
								className={cn(
									'flex flex-wrap items-center gap-x-2',
									!b.included && 'line-through decoration-1',
									b.included && b.highlight && 'font-medium',
								)}
							>
								{b.text}
								{b.included && b.comingSoon ? (
									<span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-900">
										Soon
									</span>
								) : null}
							</span>
						</li>
					))}
				</ul>

				{action ? <div className="pt-1">{action}</div> : null}
			</CardContent>
		</Card>
	)
}

function UsageRow({
	label,
	used,
	limit,
	format,
}: {
	label: string
	used: number
	limit: number
	format: (n: number) => string
}) {
	const pct = limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100))
	const isHigh = pct >= 80
	return (
		<div>
			<div className="flex items-baseline justify-between gap-2">
				<span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
					{label}
				</span>
				<span
					className={
						isHigh
							? 'text-xs tabular-nums text-amber-700'
							: 'text-xs tabular-nums text-muted-foreground'
					}
				>
					{format(used)} / {format(limit)}
				</span>
			</div>
			<div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
				<div
					className={
						isHigh
							? 'h-full bg-amber-500 transition-[width]'
							: 'h-full bg-primary transition-[width]'
					}
					style={{ width: `${pct}%` }}
				/>
			</div>
		</div>
	)
}

export function BillingSuccessPage() {
	const billing = trpc.billing.getPlan.useQuery(undefined, {
		refetchInterval: (query) => {
			const data = query.state.data
			return data?.plan === 'pro' ? false : 1000
		},
		// Don't retry forever on auth/server failures — surface them.
		retry: 2,
	})
	const [showDelayBanner, setShowDelayBanner] = useState(false)

	// External-state sync: after 10s of polling, surface the delay banner.
	useEffect(() => {
		const timer = setTimeout(() => setShowDelayBanner(true), 10_000)
		return () => clearTimeout(timer)
	}, [])

	// External-state sync: once the server-side flip lands (Stripe webhook or
	// mock-mode mutation), hard-navigate so caches reset against the new plan.
	useEffect(() => {
		if (billing.data?.plan === 'pro') {
			window.location.href = '/app'
		}
	}, [billing.data?.plan])

	if (billing.isError) {
		return (
			<div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center">
				<Alert variant="destructive" className="text-left">
					<AlertCircle />
					<AlertTitle>Couldn't confirm your subscription</AlertTitle>
					<AlertDescription>
						{billing.error?.message ?? 'Unknown error'} — refresh in a minute,
						or contact support if you were charged.
					</AlertDescription>
				</Alert>
			</div>
		)
	}

	return (
		<div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center">
			<div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
				<Sparkles className="size-6" />
			</div>
			<h1 className="text-lg font-semibold">Activating your subscription…</h1>
			<p className="text-sm text-muted-foreground">
				This takes a couple of seconds. We're confirming with Stripe.
			</p>
			{showDelayBanner ? (
				<Alert className="text-left">
					<AlertCircle />
					<AlertTitle>Taking longer than usual</AlertTitle>
					<AlertDescription>
						Payment received — activation is just delayed. Refresh in a minute,
						or open the app and it'll appear shortly.
					</AlertDescription>
				</Alert>
			) : null}
		</div>
	)
}
